/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import React, { useRef, useEffect } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
	Card,
	PageHeader,
	RevealOnScroll,
} from "@/pages/About/VisionMission.page-primitives";
import BlurText from "./Text/BlurText";

if (typeof window !== "undefined") {
	gsap.registerPlugin(ScrollTrigger);
}

// ================= TYPES =================
interface MissionPoint {
	title: string;
	image: string;
	description: string;
	stats?: { label: string; value: string }[];
}

interface VisionMissionData {
	vision: {
		title: string;
		content: string;
		image?: string;
	};
	mission: {
		title: string;
		intro: string;
		points: MissionPoint[];
	};
}

// ================= MISSION CARD =================
const MissionCard = ({ point, index }: any) => {
	const cardRef = useRef<HTMLDivElement>(null);
	const glowRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!cardRef.current) return;

		gsap.fromTo(
			cardRef.current,
			{ y: 80, opacity: 0, scale: 0.96 },
			{
				y: 0,
				opacity: 1,
				scale: 1,
				duration: 0.8,
				delay: index * 0.1,
				ease: "power3.out",
				scrollTrigger: {
					trigger: cardRef.current,
					start: "top bottom-=100",
				},
			},
		);
	}, []);

	const handleMouseMove = (e: any) => {
		const rect = cardRef.current?.getBoundingClientRect();
		if (!rect || !glowRef.current) return;

		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		gsap.to(glowRef.current, {
			x: x - 80,
			y: y - 80,
			duration: 0.3,
			ease: "power2.out",
		});
	};

	return (
		<div
			ref={cardRef}
			onMouseMove={handleMouseMove}
			className="group relative h-[420px] rounded-3xl overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500">
			{/* IMAGE (FIXED - FULL VISIBILITY) */}
			<div
				className="absolute inset-0 transition-transform duration-[1000ms] ease-out group-hover:scale-105"
				style={{
					backgroundImage: `url(${point.image})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
				}}
			/>

			{/* SIMPLE BOTTOM OVERLAY (ONLY FOR TEXT READABILITY) */}
			<div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />

			{/* SUBTLE GLOW */}
			<div
				ref={glowRef}
				className="absolute w-[160px] h-[160px] rounded-full blur-2xl opacity-0 group-hover:opacity-20 transition duration-500 bg-blue-500"
			/>

			{/* CONTENT */}
			<div className="relative h-full flex flex-col justify-end p-5 sm:p-6 text-white">
				{/* TITLE */}
				<h3 className="text-xl sm:text-2xl font-bold mb-2 transition-all duration-500 lg:group-hover:-translate-y-3">
					{point.title}
				</h3>

				{/* DESCRIPTION — always visible on touch devices, where the hover
				    reveal below never fires; the desktop reveal is unchanged. */}
				<p className="text-sm text-white/80 opacity-100 transition duration-500 lg:opacity-0 lg:group-hover:opacity-100">
					{point.description}
				</p>

				{/* STATS */}
				<div className="mt-4 flex gap-4 opacity-100 transition duration-500 lg:opacity-0 lg:group-hover:opacity-100">
					{point.stats?.map((s: any, i: number) => (
						<div key={i}>
							<div className="text-lg font-bold text-blue-300">{s.value}</div>
							<div className="text-xs text-white/60">{s.label}</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

// ================= VISION =================
const VisionSection = ({ title, content, image }: any) => {
	return (
		<section className="relative flex items-center justify-center overflow-hidden pt-16 pb-10 sm:pt-32">
			{/* CONTENT — `px-20` left only ~160px of usable width on a 320px
			    screen; the padding now scales up to the original value at `lg`. */}
			<div className="relative max-w-7xl px-4 py-6 text-center sm:px-8 sm:py-10 lg:px-20">
				{/* IMAGE BG
				{image && (
					<video
						className="absolute inset-0 w-full h-full rounded-2xl object-cover opacity-40 "
						autoPlay
						muted
						loop
						playsInline>
						<source
							src={image}
							type="video/mp4"
						/>
						Your browser does not support the video tag.
					</video>
				)} */}

				<Card className="overflow-hidden border-[#3b0092]/10 bg-gradient-to-br from-[#3b0092]/10 via-white to-[#f9a93a]/20">
					<h1 className="text-2xl font-semibold tracking-[-0.04em] py-4 sm:py-8 sm:text-4xl lg:text-[3rem] lg:leading-[1.18]">
						{title}
					</h1>

					<p className="text-sm leading-7 text-slate-700 sm:text-xl sm:leading-9">
						{content}{" "}
					</p>
				</Card>
			</div>
		</section>
	);
};

// ================= MAIN =================
const VisionMissionSection = ({ data }: { data: VisionMissionData }) => {
	const ref = useRef(null);
	const controls = useAnimation();
	const inView = useInView(ref, { once: true });

	useEffect(() => {
		if (inView) controls.start({ opacity: 1, y: 0 });
	}, [inView]);

	return (
		<div className="bg-white text-slate-900">
			{/* VISION */}
			<VisionSection {...data.vision} />

			{/* MISSION */}
			<section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-24">
				<motion.div
					ref={ref}
					initial={{ opacity: 0, y: 40 }}
					animate={controls}
					className="text-center mb-16">
					<h2 className="text-4xl md:text-5xl font-bold mb-6"></h2>

					<p className="text-slate-600 max-w-2xl mx-auto"></p>
				</motion.div>
				<RevealOnScroll>
					<PageHeader
						title={data.mission.title}
						description={data.mission.intro}
						align="center"
						className="py-14 text-center items-center "
					/>
				</RevealOnScroll>

				<div className="grid gap-6 sm:gap-10 md:grid-cols-2">
					{data.mission.points.map((p, i) => (
						<MissionCard
							key={i}
							point={p}
							index={i}
						/>
					))}
				</div>
			</section>
		</div>
	);
};

export default VisionMissionSection;
